import React, { useEffect, useState, useCallback, useRef } from "react"
import { Page, PermissionCard } from "@src/components"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { getDevice } from "@src/api"
import { getFleetsUuidFences } from "@src/api"
import type { FENCE } from "@src/settings/geofence/types"
import { useTranslation } from "react-i18next"
import { usePartner } from "@src/partner"
import { Map } from "@src/device/devicedetail/tabs/components"
import { useFleets } from "@src/settings"
import { Alert } from "react-bootstrap"

export const MapScreen: React.FC = () => {
  const { t } = useTranslation()
  const { partner, permissions } = usePartner()
  const [devices, setDevices] = useState<SLIM_DEVICE_TYPE[]>([])
  const { fleet } = useFleets()
  const [loading, setLoading] = useState<boolean>(false)
  const [totalDevices, setTotalDevices] = useState<number>(0)
  const [fences, setFences] = useState<FENCE[] | undefined>(undefined)
  const devicesRequestIdRef = useRef(0)
  const fencesRequestIdRef = useRef(0)

  const fetchDevices = useCallback(async () => {
    setLoading(true)
    const requestId = ++devicesRequestIdRef.current
    const { response, data } = await getDevice(partner?.uuid, fleet && fleet.uuid, {
      per_page: 500,
      sort_by: "last_position_timestamp",
      sort_desc: true,
    })
    if (requestId !== devicesRequestIdRef.current) return
    if (response.ok) {
      setDevices(data.devices)
      setTotalDevices(Number(data.meta.total) || 0)
    }
    setLoading(false)
  }, [partner, fleet])

  useEffect(() => {
    if (partner) {
      fetchDevices()
    }
  }, [partner, fetchDevices])

  useEffect(() => {
    const loadFences = async () => {
      if (!partner || !fleet) {
        setFences(undefined)
        return
      }
      const requestId = ++fencesRequestIdRef.current
      const { response, data } = await getFleetsUuidFences(partner.uuid, fleet.uuid)
      if (requestId !== fencesRequestIdRef.current) return
      if (response.ok) {
        setFences(data?.fences || [])
      } else {
        setFences(undefined)
      }
    }
    loadFences()
  }, [partner, fleet])

  const mapDevices = devices.filter((device) => device.position !== null && !device.position.error)
  const mapDevicePositions = mapDevices.map((device) => device.position)

  if (!permissions?.can_see_map) return <PermissionCard />

  return (
    <Page
      showFleet
      showHeader
      headerValue={t("sidebar.maps")}
      onSelectFleet={() => {
        setLoading(true)
        setDevices([])
        setTotalDevices(0)
      }}
    >
      {totalDevices > 500 && (
        <Alert className="py-2 px-3 mb-2" variant="warning">
          {t("map.limit_500_warning")}
        </Alert>
      )}
      <Map
        position={mapDevicePositions[0]}
        datapoints={mapDevicePositions}
        showRoute={false}
        showMultipleMarkers
        loadDevices={loading}
        mapDevices={mapDevices}
        fences={fences}
      />
    </Page>
  )
}
